import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Button,
  HStack,
  Input,
  Drawer,
  Textarea,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';

import { toaster } from '#components/toaster/toaster';
import { UPSERT_PROMPT } from '#graphql/mutations/upsert-prompt';
import { GET_PROMPTS } from '#graphql/queries/prompts';

interface UpsertPromptDrawerProps {
  /** Whether the drawer is open */
  open: boolean;
  /** Optional prompt ID to edit. If not provided, creates a new prompt */
  promptId?: string;
  /** Callback when the drawer is closed */
  onClose?: () => void;
}

export function UpsertPromptDrawer({
  open,
  promptId,
  onClose,
}: UpsertPromptDrawerProps) {
  const [promptName, setPromptName] = useState('');
  const [promptValue, setPromptValue] = useState('');

  const { data: promptsData } = useQuery(GET_PROMPTS);
  const [upsertPrompt, { loading: isSaving }] = useMutation(UPSERT_PROMPT);

  // Find the prompt to edit if promptId is provided
  const editingPrompt = promptId
    ? promptsData?.prompts?.prompts?.find((prompt) => prompt.id === promptId)
    : null;

  // Update form when editingPrompt changes
  useEffect(() => {
    if (editingPrompt) {
      setPromptName(editingPrompt.id);
      setPromptValue(editingPrompt.value);
    } else {
      setPromptName('');
      setPromptValue('');
    }
  }, [editingPrompt]);

  const handleSavePrompt = async () => {
    if (!promptName.trim() || !promptValue.trim()) {
      toaster.create({
        title: 'Validation Error',
        description: 'Please fill in both prompt name and value fields',
        type: 'error',
        duration: 5000,
        closable: true,
      });
      return;
    }

    try {
      await upsertPrompt({
        variables: {
          input: {
            id: promptName.trim(),
            value: promptValue.trim(),
          },
        },
      });

      toaster.create({
        title: 'Success',
        description: `Prompt '${promptName}' ${editingPrompt ? 'updated' : 'created'} successfully!`,
        type: 'success',
        duration: 5000,
        closable: true,
      });

      handleCloseModal();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Network error';
      toaster.create({
        title: 'Error',
        description: `Error saving prompt: ${errorMessage}`,
        type: 'error',
        duration: 7000,
        closable: true,
      });
    }
  };

  const handleCloseModal = () => {
    setPromptName('');
    setPromptValue('');
    onClose?.();
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleCloseModal();
      }}
      placement="end"
      size="lg"
    >
      <Drawer.Backdrop />
      <Drawer.Positioner>
        <Drawer.Content>
          <Drawer.Header>
            <Heading size="md" color="blue.500">
              {editingPrompt ? 'Edit Prompt' : 'Create New Prompt'}
            </Heading>
          </Drawer.Header>
          <Drawer.Body display="flex" flexDirection="column">
            <VStack gap={4} align="stretch" flex={1}>
              <Box>
                <Text fontSize="sm" color="fg.muted" mb={1}>
                  Prompt ID
                </Text>
                <Input
                  value={promptName}
                  onChange={(e) => setPromptName(e.target.value)}
                  placeholder="e.g., summarize-transcript"
                  readOnly={!!editingPrompt}
                  bg={editingPrompt ? 'bg.subtle' : 'bg'}
                />
              </Box>
              <Box flex={1} display="flex" flexDirection="column">
                <Text fontSize="sm" color="fg.muted" mb={1}>
                  Prompt Value
                </Text>
                <Textarea
                  value={promptValue}
                  onChange={(e) => setPromptValue(e.target.value)}
                  placeholder="Enter your prompt template with variables like <%= userId %> and <%= document?.value %>"
                  flex={1}
                  resize="vertical"
                />
              </Box>
            </VStack>
          </Drawer.Body>
          <Drawer.Footer>
            <HStack gap={2}>
              <Button onClick={handleCloseModal} variant="outline">
                Cancel
              </Button>
              <Button
                onClick={handleSavePrompt}
                colorScheme="blue"
                loading={isSaving}
                disabled={!promptName.trim() || !promptValue.trim()}
              >
                {isSaving
                  ? 'Saving...'
                  : editingPrompt
                    ? 'Update Prompt'
                    : 'Create Prompt'}
              </Button>
            </HStack>
          </Drawer.Footer>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
}
